import { FC } from 'react';
import { IonContent } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { Modal } from '../../components/Modal';
import { BRAND, CHART_INK, DOMAIN_STATE_COLOR, STATUS } from './agencyChartTheme';
import {
    describeNoScoreReason,
    describeWellbeing,
    DOMAIN_LABEL,
    FULL_SCORE,
    IClientWellbeing,
    IDomainNote,
    IDomainReading,
    WELLBEING_EXPLAINER,
    IHistoryProgress,
    describeHistoryRule,
    clientDisplayName
} from '@innerhive-internal/innerhive-common';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { PersonAvatar } from '../../avatar/Avatars';
import { OrganizationInsightsState } from '../OrganizationInsightsState';
import { ClientWellbeingHex } from './ClientWellbeingHex';
import { InsightsInfoTip } from './InsightsInfoTip';
import { NeedsHistory } from './NeedsHistory';

const useStyles = createUseStyles({
    // full screen on a phone, so the last of it scrolls clear of the system navigation bar
    content: {
        '--padding-bottom': 'var(--ion-safe-area-bottom, 0px)'
    },

    body: {
        padding: '4px 18px 26px'
    },

    who: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginBottom: 4
    },

    whoName: {
        flex: 1,
        minWidth: 0
    },

    // The name itself opens the profile. A button at the bottom of a long modal is easy to miss, and
    // the name is where a reader's attention already is once they know who they are looking at.
    nameButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        flex: 1,
        minWidth: 0,
        padding: '2px 6px 2px 0',
        border: 'none',
        borderRadius: 6,
        background: 'none',
        font: 'inherit',
        textAlign: 'left' as const,
        cursor: 'pointer',

        '&:hover, &:focus-visible': {
            color: BRAND.deeperTeal,

            '& $caret': {
                transform: 'translateX(2px)'
            }
        }
    },

    caret: {
        flexShrink: 0,
        fontSize: 19,
        lineHeight: 1,
        color: BRAND.coral,
        transition: 'transform 140ms ease'
    },

    profileLink: {
        display: 'inline-block',
        marginTop: 14,
        padding: '8px 16px',
        borderRadius: 20,
        border: `1px solid ${BRAND.tealEdge}`,
        background: 'var(--ion-background-color)',
        font: 'inherit',
        fontSize: 13,
        fontWeight: 500,
        color: BRAND.deepTeal,
        cursor: 'pointer',

        '&:hover': {
            background: BRAND.tealWash,
            borderColor: BRAND.deepTeal
        }
    },

    profileRow: {
        textAlign: 'center' as const
    },

    name: {
        fontSize: 17,
        fontWeight: 600,
        color: BRAND.deepTeal
    },

    summary: {
        marginTop: 10,
        fontSize: 13.5,
        lineHeight: 1.5,
        textAlign: 'center' as const,
        color: CHART_INK.strongLabel
    },

    sectionHead: {
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        margin: '22px 0 10px',
        fontSize: 14,
        fontWeight: 600,
        color: BRAND.deepTeal
    },

    headMark: {
        width: 3,
        height: 14,
        borderRadius: 2,
        background: BRAND.coral
    },

    facet: {
        padding: '12px 0',
        borderTop: `1px solid ${BRAND.tealEdge}`,

        '&:first-child': {
            borderTop: 'none'
        }
    },

    facetHead: {
        display: 'flex',
        alignItems: 'center',
        gap: 10
    },

    facetName: {
        flex: 1,
        fontSize: 13.5,
        fontWeight: 500,
        color: CHART_INK.strongLabel
    },

    facetScore: {
        fontSize: 12.5,
        color: CHART_INK.label,
        fontVariantNumeric: 'tabular-nums'
    },

    bar: {
        position: 'relative',
        height: 8,
        marginTop: 7,
        borderRadius: 4,
        background: 'var(--ion-color-step-100)',
        overflow: 'hidden'
    },

    barFill: {
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        borderRadius: 4
    },

    notes: {
        margin: '10px 0 0',
        padding: 0,
        listStyle: 'none',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 8
    },

    note: {
        display: 'flex',
        gap: 9,
        fontSize: 12.5,
        lineHeight: 1.5,
        color: CHART_INK.label
    },

    noteMark: {
        flexShrink: 0,
        width: 7,
        height: 7,
        marginTop: 6,
        borderRadius: '50%'
    },

    noteTitle: {
        fontWeight: 500,
        color: CHART_INK.strongLabel
    },

    noteWhen: {
        color: CHART_INK.label
    },

    quiet: {
        marginTop: 8,
        fontSize: 12.5,
        color: CHART_INK.label
    }
});

const when = (isoDate: string): string => {
    const day = new Date(isoDate);
    if (isNaN(day.getTime())) {
        return '';
    }
    return day.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const NoteList: FC<{ notes: IDomainNote[] }> = ({ notes }) => {
    const classes = useStyles();

    return (
        // Each note names what was observed about this person, so it stays out of
        // session replay.
        <ul className={classes.notes}>
            {notes.map((note, index) => (
                <li key={`${note.observedOn}-${index}`} className={`${classes.note} mp-mask`}>
                    <span
                        className={classes.noteMark}
                        style={{ background: note.isIncident ? STATUS.incident : STATUS.declining }}
                    />
                    <span>
                        <span className={classes.noteTitle}>{note.title}</span>
                        {note.narrative ? ` ${note.narrative}` : ''}
                        <span className={classes.noteWhen}> ({when(note.observedOn)})</span>
                    </span>
                </li>
            ))}
        </ul>
    );
};

/** Incidents recorded against no part of daily life, which the diagram has no wedge for. */
const UnplacedIncidents: FC<{ notes: IDomainNote[] }> = ({ notes }) => {
    const classes = useStyles();

    if (notes.length === 0) {
        return null;
    }

    return (
        <div className={classes.facet} data-id="wellbeing-unplaced-incidents">
            <div className={classes.facetHead}>
                <span className={classes.facetName}>Not Tied to One Part of Life</span>
            </div>

            <NoteList notes={notes} />
        </div>
    );
};

/** One part of daily life, its score when there is one, and the notes behind it. */
const Facet: FC<{
    reading: IDomainReading;
    /**
     * False while the caseload is too young to score, or for a client who cannot be compared with
     * their own baseline: a full score then only means nothing was written down.
     */
    scored: boolean;
}> = ({ reading, scored }) => {
    const classes = useStyles();

    return (
        <div className={classes.facet} data-id="wellbeing-facet" data-domain={reading.domain}>
            <div className={classes.facetHead}>
                <span className={classes.facetName}>{DOMAIN_LABEL[reading.domain]}</span>
                {scored && <span className={classes.facetScore} data-id="wellbeing-facet-score">{reading.score} / {FULL_SCORE}</span>}
            </div>

            {scored && (
                <div className={classes.bar} data-id="wellbeing-facet-bar">
                    <div
                        className={classes.barFill}
                        style={{ width: `${reading.score}%`, background: DOMAIN_STATE_COLOR[reading.state] }}
                    />
                </div>
            )}

            {reading.notes.length === 0
                ? <div className={classes.quiet}>Nothing raised about this recently.</div>
                : <NoteList notes={reading.notes} />}
        </div>
    );
};

/**
 * Who the detail is about, with the way into their profile when there is one. Its tooltip explains the
 * diagram, or while the caseload is too young to draw one, what it is waiting for.
 */
const ClientHeading: FC<{
    wellbeing: IClientWellbeing;
    history: IHistoryProgress;
    onOpenProfile?: (careMapId: string) => void;
}> = ({ wellbeing, history, onOpenProfile }) => {
    const classes = useStyles();
    const insightsState = useInstance(OrganizationInsightsState);

    return (
        <div className={classes.who}>
            <PersonAvatar size={32} identity={insightsState.getClientAvatar(wellbeing.careMapId)} name={wellbeing.clientName} />

            {onOpenProfile
                ? (
                    <button
                        type="button"
                        className={classes.nameButton}
                        data-id="wellbeing-open-profile-name"
                        title="Open Care Profile"
                        onClick={() => onOpenProfile(wellbeing.careMapId)}
                    >
                        <span className={`${classes.name} ${classes.whoName} mp-mask`}>{clientDisplayName(wellbeing.clientName)}</span>
                        <span className={classes.caret} aria-hidden="true">&rsaquo;</span>
                    </button>
                )
                : <span className={`${classes.name} ${classes.whoName} mp-mask`}>{clientDisplayName(wellbeing.clientName)}</span>}

            <InsightsInfoTip
                label="About this client's reading"
                summary={history.hasEnough ? describeNoScoreReason(wellbeing) : undefined}
            >
                {history.hasEnough ? WELLBEING_EXPLAINER : describeHistoryRule(history)}
            </InsightsInfoTip>
        </div>
    );
};

/**
 * One client's week in full: the diagram at size, then every part of daily life with the notes
 * behind it.
 *
 * The point of the detail view is the why. A dispatcher looking at a short wedge needs the sentence
 * somebody actually wrote, not a second summary of it, so each facet lists its notes verbatim with
 * the date they were recorded.
 *
 * With a score, facets are listed worst first rather than in the diagram's clockwise order. The
 * diagram is for recognising a shape; this list is a worklist, and a worklist starts at the top.
 * Without one (a caseload too young to score, or a client who cannot be compared with their own
 * baseline) they keep the diagram's order and show no score.
 */
export const ClientWellbeingModal: FC<{
    wellbeing?: IClientWellbeing;
    history: IHistoryProgress;
    /** Absent in demo mode: the invented clients have no care map to open. */
    onOpenProfile?: (careMapId: string) => void;
    onClose: () => void;
}> = ({ wellbeing, history, onOpenProfile, onClose }) => {
    const classes = useStyles();

    // Mounted whether or not a client is open, so Ionic can animate the dismissal rather than having
    // the element pulled out from under it mid-transition.
    const scored = history.hasEnough && wellbeing?.overall !== 'unscored';
    const readings = wellbeing ? [...wellbeing.readings] : [];
    const facets = scored ? readings.sort((a, b) => a.score - b.score) : readings;

    return (
        <Modal
            isOpen={Boolean(wellbeing)}
            title="How are they doing?"
            data-id="client-wellbeing-modal"
            desktopMaxWidth={560}
            closeHandler={onClose}
        >
            <IonContent className={classes.content}>
                {wellbeing && (
                <div className={classes.body}>
                    <ClientHeading wellbeing={wellbeing} history={history} onOpenProfile={onOpenProfile} />

                    <NeedsHistory history={history} panel="client-wellbeing">
                        <ClientWellbeingHex wellbeing={wellbeing} emphasis />

                        <div className={classes.summary}>{describeWellbeing(wellbeing)}</div>
                    </NeedsHistory>

                    <div className={classes.sectionHead}>
                        <span className={classes.headMark} />
                        {scored ? 'What Sits Behind the Number' : 'Recent Callouts'}
                    </div>

                    <UnplacedIncidents notes={wellbeing.unplacedIncidents} />

                    {facets.map(reading => (
                        <Facet key={reading.domain} reading={reading} scored={scored} />
                    ))}

                    {onOpenProfile && (
                        <div className={classes.profileRow}>
                            <button
                                type="button"
                                className={classes.profileLink}
                                data-id="wellbeing-open-profile"
                                onClick={() => onOpenProfile(wellbeing.careMapId)}
                            >
                                Open Care Profile
                            </button>
                        </div>
                    )}
                </div>
                )}
            </IonContent>
        </Modal>
    );
};
